// Canada411 · manual-lookup dialog
// Opens a deep-link to canada411.ca in the CSF's browser, then lets
// the CSF paste back what they FOUND. Backend computes match
// confidence + routes through compliance engine.
//
// ZERO fabrication: if the CSF didn't find anything, nothing is
// persisted.
import { useState } from "react";
import { X, ExternalLink, ShieldCheck, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { NAVY, GOLD, SLATE, CHAMPAGNE } from
  "@/components/altiora/PremiumUI";

export default function Canada411LookupDialog({
  open, onClose, lead, record, onDone,
}) {
  const dm = lead?.decision_maker || {};
  const addr = lead?.address || {};
  const nameParts = (dm.name || "").trim().split(/\s+/);
  const [expected] = useState(() => ({
    first: nameParts[0] || "",
    last: nameParts.slice(1).join(" ") || "",
    city: addr.city || "",
    province: addr.province || "",
  }));
  const [found, setFound] = useState({
    first: nameParts[0] || "",
    last: nameParts.slice(1).join(" ") || "",
    city: addr.city || "",
    province: addr.province || "",
    civic: "",
    postal: "",
    phone: "",
    sourceUrl: "",
    documentedHomonym: false,
  });
  const [searchUrl, setSearchUrl] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  async function buildSearchUrl() {
    setError(null);
    try {
      const r = await api.get("/leads-engine/canada411/search-url", {
        params: {
          first_name: expected.first, last_name: expected.last,
          city: expected.city, province: expected.province,
        },
      });
      setSearchUrl(r.data.url);
      window.open(r.data.url, "_blank", "noopener,noreferrer");
      // Prefill sourceUrl so the CSF can just paste the exact search URL
      setFound((f) => ({ ...f, sourceUrl: r.data.url }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible d'ouvrir Canada411.");
    }
  }

  async function submitLookup() {
    setError(null);
    setLoading(true);
    try {
      // Ensure the lead is persisted first — the manual-lookup route
      // requires an existing altiora_lead_records row. If we're on a
      // detection view, `record` may be null; upsert on the fly.
      if (!record?.entity_id && lead) {
        try {
          await api.post("/leads-engine/leads/upsert", { unified: lead });
        } catch (upsertErr) {
          // If upsert fails, surface it but continue attempt (some
          // records may already exist server-side but not in this
          // client state).
          if (upsertErr?.response?.status !== 400) {
            throw upsertErr;
          }
        }
      }
      const r = await api.post("/leads-engine/canada411/manual-lookup", {
        entity_id: lead.entity_id,
        expected_first: expected.first,
        expected_last: expected.last,
        expected_city: expected.city || null,
        expected_province: expected.province || null,
        found_first: found.first,
        found_last: found.last,
        found_city: found.city || null,
        found_province: found.province || null,
        found_civic: found.civic || null,
        found_postal: found.postal || null,
        found_phone: found.phone || null,
        source_url: found.sourceUrl || null,
        documented_homonym: !!found.documentedHomonym,
      });
      setResult(r.data);
      onDone?.(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail
                 || "Échec de l'enregistrement du lookup.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(11,37,69,0.62)" }}>
      <div data-testid="canada411-dialog"
           className="w-full max-w-lg rounded-xl bg-white overflow-hidden"
           style={{ boxShadow: "0 24px 64px -20px rgba(11,37,69,0.55)" }}>
        <header className="flex items-center justify-between px-5 py-4"
                style={{ background: NAVY, color: "#FBF6EA" }}>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em]"
                 style={{ color: GOLD }}>
              Canada411 · vérification manuelle
            </div>
            <h3 className="font-serif text-[18px] mt-1">
              {dm.name || lead?.entity_name}
            </h3>
          </div>
          <button data-testid="canada411-dialog-close" onClick={onClose}
                  className="rounded-full p-1"
                  style={{ background: "rgba(251,246,234,0.14)" }}>
            <X className="h-4 w-4" style={{ color: "#FBF6EA" }} />
          </button>
        </header>

        <div className="p-5 space-y-4">
          <div className="rounded-lg p-3 text-[12px]"
               style={{ background: "#FBF6EA",
                        border: `1px solid ${CHAMPAGNE}55`, color: NAVY }}>
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em]"
                 style={{ color: GOLD }}>
              <ShieldCheck className="h-3 w-3" />
              Aucune automatisation
            </div>
            <p className="mt-1">
              ALTIORA n'interroge <strong>pas</strong> Canada411.
              Ouvrez le lien dans <em>votre</em> navigateur, consultez le
              résultat, puis rapportez uniquement ce que <strong>vous</strong>
              y avez trouvé. Si aucun résultat n'est trouvé, laissez le
              champ téléphone vide — aucune donnée ne sera fabriquée.
            </p>
          </div>

          <button data-testid="canada411-open-search"
                  onClick={buildSearchUrl}
                  className="w-full flex items-center justify-center gap-2
                             rounded-full px-4 py-2 text-[13px]"
                  style={{ background: GOLD, color: NAVY }}>
            <ExternalLink className="h-4 w-4" />
            {searchUrl ? "Rouvrir Canada411" : "Ouvrir Canada411 dans un onglet"}
          </button>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Prénom trouvé" testId="canada411-found-first"
                   value={found.first}
                   onChange={(v) => setFound({ ...found, first: v })} />
            <Field label="Nom trouvé" testId="canada411-found-last"
                   value={found.last}
                   onChange={(v) => setFound({ ...found, last: v })} />
            <Field label="Ville trouvée" testId="canada411-found-city"
                   value={found.city}
                   onChange={(v) => setFound({ ...found, city: v })} />
            <Field label="Province" testId="canada411-found-province"
                   value={found.province}
                   onChange={(v) => setFound({ ...found, province: v })} />
            <Field label="No civique" testId="canada411-found-civic"
                   value={found.civic}
                   onChange={(v) => setFound({ ...found, civic: v })} />
            <Field label="Code postal" testId="canada411-found-postal"
                   value={found.postal}
                   onChange={(v) => setFound({ ...found, postal: v })} />
            <Field label="Téléphone trouvé (facultatif)"
                   testId="canada411-found-phone"
                   value={found.phone} colSpan={2}
                   placeholder="514-555-0100"
                   onChange={(v) => setFound({ ...found, phone: v })} />
          </div>

          <label className="flex items-center gap-2 text-[11px]"
                 style={{ color: SLATE }}>
            <input type="checkbox"
                   data-testid="canada411-homonym-checkbox"
                   checked={found.documentedHomonym}
                   onChange={(e) => setFound({ ...found,
                     documentedHomonym: e.target.checked })} />
            Homonyme documenté ailleurs (pénalité -20)
          </label>

          {error && (
            <div data-testid="canada411-error"
                 className="flex items-center gap-2 text-[12px] rounded-md p-2"
                 style={{ background: "#FCE1E1", color: "#8A2E2E" }}>
              <AlertTriangle className="h-3 w-3" />
              {error}
            </div>
          )}

          {result && (
            <div data-testid="canada411-result"
                 className="rounded-lg p-3 text-[12px] space-y-1"
                 style={{ background: "#E9F3EE", border: `1px solid #BEDFCB`,
                          color: "#0F5E39" }}>
              <div>
                <strong>Match confidence :</strong> {result.match?.score}/100
                {" · "}{result.match?.band}
              </div>
              <div>
                <strong>Motifs :</strong>{" "}
                {(result.match?.reasons || []).join(", ") || "aucun"}
              </div>
              <div>
                <strong>Statut compliance :</strong>{" "}
                {result.compliance?.final_status || "aucun téléphone soumis"}
              </div>
              <div>
                <strong>Téléphone persisté :</strong>{" "}
                {result.phone_persisted ? "Oui" : "Non"}
              </div>
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 px-5 py-4"
                style={{ borderTop: `1px solid ${CHAMPAGNE}33` }}>
          <button data-testid="canada411-cancel" onClick={onClose}
                  className="text-[12px] px-3 py-1.5 rounded-full"
                  style={{ color: SLATE }}>
            Fermer
          </button>
          <button data-testid="canada411-submit"
                  onClick={submitLookup} disabled={loading}
                  className="text-[12px] px-4 py-1.5 rounded-full"
                  style={{ background: NAVY, color: "#FBF6EA",
                           opacity: loading ? 0.5 : 1 }}>
            {loading ? "Enregistrement…"
             : "Enregistrer & appliquer compliance"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId, colSpan }) {
  return (
    <label className={"flex flex-col gap-1"
                       + (colSpan === 2 ? " col-span-2" : "")}>
      <span className="text-[10px] font-mono uppercase tracking-[0.14em]"
            style={{ color: SLATE }}>{label}</span>
      <input type="text" value={value || ""} placeholder={placeholder}
             data-testid={testId}
             onChange={(e) => onChange(e.target.value)}
             className="rounded-md px-2.5 py-1.5 text-[13px]"
             style={{ border: `1px solid ${CHAMPAGNE}88`,
                      background: "#FBF6EA55", color: NAVY }} />
    </label>
  );
}
